import { arrayWithoutSubset, moveToFulfilled, moveToPending, moveToRejected, update } from '@ms/yammer-data-support';
import { Reducer } from 'redux';

import {
  SubscribeToFollowedUserNotificationsAction,
  SubscribeToFollowedUserNotificationsFulfilledAction,
  UnsubscribeToFollowedUserNotificationsAction,
  UnsubscribeToFollowedUserNotificationsFulfilledAction,
  ViewerNotificationSubscriptionsForUserAction,
} from '../../domains/viewerNotificationSubscriptions/actions.types';

import { ViewerNotificationSubscriptionsForUsers } from './types';

export type UserNotificationSubscriptionsAction =
  | SubscribeToFollowedUserNotificationsAction
  | UnsubscribeToFollowedUserNotificationsAction
  | ViewerNotificationSubscriptionsForUserAction;

const initialState: ViewerNotificationSubscriptionsForUsers = {
  pending: { byId: {} },
  fulfilled: { byId: {} },
  rejected: { byId: {} },
};

const subscribeToFollowedUserNotifications = (
  state: ViewerNotificationSubscriptionsForUsers,
  { userId, targets, viewerNotificationSubscriptions }: SubscribeToFollowedUserNotificationsFulfilledAction['payload']
) => {
  if (!targets || targets.length === 0) {
    return state;
  }

  const initialState = state.fulfilled.byId[userId];
  if (initialState) {
    const targetsExcludingExistingSubscriptions = arrayWithoutSubset(
      targets,
      initialState.viewerNotificationSubscriptions
    );

    const newState = [...initialState.viewerNotificationSubscriptions, ...targetsExcludingExistingSubscriptions];

    return update(state, userId, {
      viewerNotificationSubscriptions: newState,
    });
  }

  const initialStateExcludingTargets = arrayWithoutSubset(viewerNotificationSubscriptions, targets);

  return moveToFulfilled(state, {
    [userId]: { id: userId, viewerNotificationSubscriptions: [...initialStateExcludingTargets, ...targets] },
  });
};

const unsubscribeToFollowedUserNotifications = (
  state: ViewerNotificationSubscriptionsForUsers,
  { userId, targets, viewerNotificationSubscriptions }: UnsubscribeToFollowedUserNotificationsFulfilledAction['payload']
) => {
  if (!targets || targets.length === 0) {
    return state;
  }
  const initialState = state.fulfilled.byId[userId];
  if (initialState) {
    const updatedSubscriptions = arrayWithoutSubset(initialState.viewerNotificationSubscriptions, targets);

    return update(state, userId, { viewerNotificationSubscriptions: updatedSubscriptions });
  }

  const updatedSubscriptions = arrayWithoutSubset(viewerNotificationSubscriptions, targets);

  return moveToFulfilled(state, {
    [userId]: { id: userId, viewerNotificationSubscriptions: [...updatedSubscriptions] },
  });
};

const reducer: Reducer<ViewerNotificationSubscriptionsForUsers, UserNotificationSubscriptionsAction> = (
  state = initialState,
  action
) => {
  switch (action.type) {
    case 'VIEWER_NOTIFICATION_SUBSCRIPTIONS.SUBSCRIBE_BY_FOLLOWED_USER_PENDING':
      return moveToPending(state, [action.payload.followedUserId]);
    case 'VIEWER_NOTIFICATION_SUBSCRIPTIONS.SUBSCRIBE_BY_FOLLOWED_USER_FULFILLED':
      return subscribeToFollowedUserNotifications(state, action.payload);
    case 'VIEWER_NOTIFICATION_SUBSCRIPTIONS.SUBSCRIBE_BY_FOLLOWED_USER_REJECTED':
      return moveToRejected(state, [action.payload.followedUserId], action.payload.error);
    case 'VIEWER_NOTIFICATION_SUBSCRIPTIONS.UNSUBSCRIBE_BY_FOLLOWED_USER_PENDING':
      return moveToPending(state, [action.payload.followedUserId]);
    case 'VIEWER_NOTIFICATION_SUBSCRIPTIONS.UNSUBSCRIBE_BY_FOLLOWED_USER_FULFILLED':
      return unsubscribeToFollowedUserNotifications(state, action.payload);
    case 'VIEWER_NOTIFICATION_SUBSCRIPTIONS.UNSUBSCRIBE_BY_FOLLOWED_USER_REJECTED':
      return moveToRejected(state, [action.payload.followedUserId], action.payload.error);
    case 'VIEWER_NOTIFICATION_SUBSCRIPTIONS.LOAD_BY_FOLLOWED_USER_PENDING':
      return moveToPending(state, [action.payload]);
    case 'VIEWER_NOTIFICATION_SUBSCRIPTIONS.LOAD_BY_FOLLOWED_USER_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerNotificationSubscriptionsForUsers);
    case 'VIEWER_NOTIFICATION_SUBSCRIPTIONS.LOAD_BY_FOLLOWED_USER_REJECTED':
      return moveToRejected(state, [action.payload.followedId], action.payload.error);

    default:
      return state;
  }
};

export default reducer;
